/**
 * Notification Preferences Tab - Phase 9.3d
 *
 * Allows users to configure which notification types they receive and
 * customize threshold values for financial notifications.
 *
 * Features:
 * - Toggle individual notification types on/off
 * - Customize threshold values (overdue warning, available to bill)
 * - Reset to defaults
 *
 * Preferences are stored in Firestore at:
 * /tenants/{tenant_id}/users/{user_id}/settings/notifications
 */
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Bell, Save, RotateCcw, ChevronDown, ChevronRight } from 'lucide-react';

interface NotificationPreferences {
  enabled_types: Record<string, boolean>;
  thresholds: Record<string, number>;
}

// Notification types organized by category
const NOTIFICATION_CATEGORIES = [
  {
    id: 'permits',
    title: 'Permit Reminders',
    description: 'Get notified when permits are approaching due dates or overdue',
    types: [
      { key: 'permit_due_60', label: '60 days before due', description: 'Early planning reminder' },
      { key: 'permit_due_30', label: '30 days before due', description: 'Standard reminder' },
      { key: 'permit_due_7', label: '7 days before due', description: 'Urgent reminder' },
      { key: 'permit_overdue', label: 'Overdue permits', description: 'Permit has passed due date' },
    ],
  },
  {
    id: 'invoices',
    title: 'Invoice Reminders',
    description: 'Get notified about invoice payment deadlines',
    types: [
      { key: 'invoice_due_7', label: '7 days before due', description: 'Invoice payment deadline approaching' },
      { key: 'invoice_overdue', label: 'Overdue invoices', description: 'Invoice payment is past due' },
      { key: 'invoice_no_payment_30', label: 'No payment after 30 days', description: 'Invoice sent but no payment received' },
    ],
  },
  {
    id: 'action_items',
    title: 'Action Items',
    description: 'Get notified about items requiring attention',
    types: [
      { key: 'ball_in_court_7', label: 'Waiting on us > 7 days', description: 'Action needed within a week' },
      { key: 'ball_in_court_14', label: 'Waiting on us > 14 days', description: 'Escalation - action overdue' },
      { key: 'proposal_pending_30', label: 'Proposal pending > 30 days', description: 'Proposal needs follow-up' },
    ],
  },
  {
    id: 'financial',
    title: 'Financial Thresholds',
    description: 'Get notified when financial amounts exceed thresholds',
    types: [
      { key: 'overdue_threshold', label: 'Overdue invoices exceed threshold', description: 'Total overdue amount is too high' },
      { key: 'available_to_bill_threshold', label: 'Available to bill exceeds threshold', description: 'Unbilled work is accumulating' },
    ],
  },
];

// Threshold configurations
const THRESHOLDS = [
  { key: 'overdue_warning_amount', label: 'Overdue invoices warning threshold', prefix: '$', default: 25000 },
  { key: 'available_to_bill_amount', label: 'Available to bill threshold', prefix: '$', default: 10000 },
];

interface CollapsibleCategoryProps {
  category: typeof NOTIFICATION_CATEGORIES[0];
  enabledTypes: Record<string, boolean>;
  onToggle: (key: string) => void;
  isExpanded: boolean;
  onExpandToggle: () => void;
}

function CollapsibleCategory({ category, enabledTypes, onToggle, isExpanded, onExpandToggle }: CollapsibleCategoryProps) {
  const enabledCount = category.types.filter(t => enabledTypes[t.key] !== false).length;
  const totalCount = category.types.length;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onExpandToggle}
        className="w-full flex items-center gap-3 p-4 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
        aria-expanded={isExpanded}
      >
        {isExpanded ? (
          <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-5 h-5 text-gray-500 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold text-gray-900">{category.title}</h4>
          <p className="text-xs text-gray-600 mt-0.5">{category.description}</p>
        </div>
        <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded-full">
          {enabledCount}/{totalCount} enabled
        </span>
      </button>
      {isExpanded && (
        <div className="p-4 border-t border-gray-200 bg-white space-y-3">
          {category.types.map((type) => (
            <div key={type.key} className="flex items-start gap-3">
              <Checkbox
                id={type.key}
                checked={enabledTypes[type.key] !== false}
                onCheckedChange={() => onToggle(type.key)}
                className="mt-0.5"
              />
              <div className="flex-1">
                <Label htmlFor={type.key} className="text-sm font-medium cursor-pointer">
                  {type.label}
                </Label>
                <p className="text-xs text-gray-500">{type.description}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function NotificationPreferencesTab() {
  const queryClient = useQueryClient();

  // Fetch current preferences
  const { data: preferences, isLoading, error } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => api.get('/api/notifications/preferences') as Promise<NotificationPreferences>,
  });

  // Local form state
  const [enabledTypes, setEnabledTypes] = useState<Record<string, boolean>>({});
  const [thresholds, setThresholds] = useState<Record<string, number>>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Track which categories are expanded
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    permits: true,
    invoices: true,
    action_items: true,
    financial: true,
  });

  // Initialize form state when data loads
  useEffect(() => {
    if (preferences) {
      setEnabledTypes(preferences.enabled_types || {});
      setThresholds(preferences.thresholds || {});
      setHasChanges(false);
    }
  }, [preferences]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: NotificationPreferences) =>
      api.put('/api/notifications/preferences', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      toast.success('Preferences saved');
      setHasChanges(false);
    },
    onError: () => toast.error('Failed to save preferences'),
  });

  // Reset mutation
  const resetMutation = useMutation({
    mutationFn: () => api.post('/api/notifications/preferences/reset') as Promise<{ success: boolean; preferences: NotificationPreferences }>,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      if (data.preferences) {
        setEnabledTypes(data.preferences.enabled_types || {});
        setThresholds(data.preferences.thresholds || {});
      }
      toast.success('Preferences reset to defaults');
      setHasChanges(false);
    },
    onError: () => toast.error('Failed to reset preferences'),
  });

  const handleToggleType = (key: string) => {
    setEnabledTypes((prev) => ({
      ...prev,
      [key]: prev[key] === false ? true : false,
    }));
    setHasChanges(true);
  };

  const handleThresholdChange = (key: string, value: string) => {
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 0) {
      setThresholds((prev) => ({
        ...prev,
        [key]: numValue,
      }));
      setHasChanges(true);
    }
  };

  const handleSave = () => {
    saveMutation.mutate({
      enabled_types: enabledTypes,
      thresholds: thresholds,
    });
  };

  const handleReset = () => {
    if (window.confirm('Reset all notification preferences to defaults?')) {
      resetMutation.mutate();
    }
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600">
        Failed to load notification preferences. Please try again.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b">
        <Bell className="w-5 h-5 text-slate-600" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Notification Preferences</h2>
          <p className="text-sm text-gray-600 mt-1">
            Choose which notifications you want to receive
          </p>
        </div>
      </div>

      {/* Notification Categories */}
      <div className="space-y-3">
        {NOTIFICATION_CATEGORIES.map((category) => (
          <CollapsibleCategory
            key={category.id}
            category={category}
            enabledTypes={enabledTypes}
            onToggle={handleToggleType}
            isExpanded={expandedCategories[category.id] ?? true}
            onExpandToggle={() => toggleCategory(category.id)}
          />
        ))}
      </div>

      {/* Threshold Values */}
      <div className="border border-gray-200 rounded-lg p-4 bg-white">
        <h4 className="text-sm font-semibold text-gray-900 mb-3">Threshold Values</h4>
        <p className="text-xs text-gray-600 mb-4">
          Customize when financial threshold notifications trigger
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {THRESHOLDS.map((threshold) => (
            <div key={threshold.key}>
              <Label htmlFor={threshold.key} className="text-sm font-medium">
                {threshold.label}
              </Label>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-gray-500">{threshold.prefix}</span>
                <Input
                  id={threshold.key}
                  type="number"
                  value={thresholds[threshold.key] ?? threshold.default}
                  onChange={(e) => handleThresholdChange(threshold.key, e.target.value)}
                  min="0"
                  step="1000"
                  className="w-32"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center gap-3 pt-4 border-t">
        <Button
          onClick={handleSave}
          disabled={saveMutation.isPending || !hasChanges}
          className="inline-flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {saveMutation.isPending ? 'Saving...' : hasChanges ? 'Save Preferences' : 'Saved'}
        </Button>
        <Button
          variant="outline"
          onClick={handleReset}
          disabled={resetMutation.isPending}
          className="inline-flex items-center gap-2"
        >
          <RotateCcw className="w-4 h-4" />
          {resetMutation.isPending ? 'Resetting...' : 'Reset to Defaults'}
        </Button>
      </div>
    </div>
  );
}
